import React from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { SpaceProps } from 'styled-system';
import Box from 'ui/Box';
import Title from 'ui/Typography/Title';
import Body from 'ui/Typography/Body';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import SegmentedControl from '@ingka/segmented-control';

const Wrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  kompis-toggle {
    padding: ${({ theme }) => theme.space.xxs} 0;
    white-space: nowrap;
  }
`;

const ToggleWrapper = styled.div<{ $noMargin?: boolean }>`
  margin-top: ${props => (props.$noMargin ? '0px' : props.theme.space.md)};
`;

type ToggleSize = {
  value: number;
  text: string;
  unit: string;
};

type SizeToggleProps = SpaceProps & {
  title?: string;
  boldTitle?: boolean;
  mixedSizes?: boolean;
  currentSize: number;
  sizes: ToggleSize[];
  disabled?: boolean;
  onChangeSize: (size: number) => void;
  noMargin?: boolean;
  dataTestId?: string;
};

const SizeToggle = ({
  title,
  boldTitle = true,
  mixedSizes = false,
  sizes,
  currentSize,
  disabled,
  onChangeSize,
  noMargin,
  dataTestId,
  ...props
}: SizeToggleProps) => {
  const { formatMessage } = useIntl();

  const items = sizes.map(size => {
    return { label: `${size.text} ${size.unit}`, icon: '' };
  });

  let selectedIndex = sizes.findIndex((size: ToggleSize) => size.value === currentSize);

  if (mixedSizes) {
    const label = formatMessage({ id: 'storageone.changeSizeMixed' });
    items.push({ label: label, icon: '' });
    selectedIndex = items.length - 1;
  }

  const onToggleSelect = (index: number) => {
    if (index >= sizes.length) return;
    const value = sizes[index].value;
    onChangeSize(value);
  };

  return (
    selectedIndex >= 0 && (
      <Wrapper {...props}>
        {title && boldTitle && <Title type={ThemeFontStyleTypeEnum.headingXSmall}>{title}</Title>}
        {title && !boldTitle && <Body type={ThemeFontStyleTypeEnum.bodyMedium}>{title}</Body>}
        <ToggleWrapper $noMargin={noMargin} data-testid={dataTestId}>
          <SegmentedControl
            items={items}
            activeIndex={selectedIndex}
            disabled={disabled}
            onClick={(_e, value) => onToggleSelect(value)}
          />
        </ToggleWrapper>
      </Wrapper>
    )
  );
};

export default SizeToggle;
